import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, useTheme, Button, CircularProgress, Chip, Divider,
} from '@mui/material';
import {
  Sync as SyncIcon,
  CheckCircle as OkIcon,
  Error as ErrorIcon,
  CloudDownload as CloudIcon,
} from '@mui/icons-material';
import { AdminService, MitreSyncStatus, MitreSyncResult } from '../services/adminService';
import { getRelativeTimeString } from '../utils/dateUtils';

// ── MITRE section ─────────────────────────────────────────────────────────────

const MitreSection: React.FC = () => {
  const theme = useTheme();
  const [status, setStatus]   = useState<MitreSyncStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [error, setError]     = useState<string | null>(null);

  const loadStatus = useCallback(async () => {
    try {
      const s = await AdminService.getMitreStatus();
      setStatus(s);
    } catch {
      setError('Failed to load MITRE sync status.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  const handleSync = async () => {
    setSyncing(true);
    setError(null);
    try {
      const result: MitreSyncResult = await AdminService.triggerMitreSync();
      setStatus({
        synced: true,
        attack_version: result.version,
        technique_count: result.technique_count,
        last_synced_at: new Date().toISOString(),
        status: 'ok',
        error_message: null,
        duration_ms: result.duration_ms,
      });
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Sync failed.';
      setError(msg);
    } finally {
      setSyncing(false);
    }
  };

  const statusColor =
    !status?.synced          ? theme.palette.text.disabled :
    status.status === 'ok'   ? theme.palette.success.main  :
                               theme.palette.error.main;

  const StatusIcon =
    !status?.synced        ? CloudIcon :
    status.status === 'ok' ? OkIcon    :
                             ErrorIcon;

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ color: theme.palette.text.secondary, mb: 2, fontSize: '0.7rem', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
        MITRE ATT&amp;CK Dataset
      </Typography>

      <Box sx={{ p: 2.5, borderRadius: 1.5, backgroundColor: theme.palette.background.paper, border: `1px solid ${theme.palette.divider}` }}>
        {loading ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <CircularProgress size={14} />
            <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>Loading status…</Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap' }}>
            {/* Status indicator */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <StatusIcon sx={{ fontSize: 16, color: statusColor }} />
              <Typography variant="body2" sx={{ color: statusColor, fontWeight: 600, fontSize: '0.78rem' }}>
                {!status?.synced ? 'Not synced yet' : status.status === 'ok' ? 'OK' : 'Error'}
              </Typography>
            </Box>

            {/* Version */}
            {status?.attack_version && (
              <Box>
                <Typography variant="caption" sx={{ color: theme.palette.text.disabled, display: 'block', fontSize: '0.62rem' }}>VERSION</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.78rem' }}>{status.attack_version}</Typography>
              </Box>
            )}

            {/* Technique count */}
            {status?.technique_count != null && (
              <Box>
                <Typography variant="caption" sx={{ color: theme.palette.text.disabled, display: 'block', fontSize: '0.62rem' }}>TECHNIQUES</Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.78rem' }}>{status.technique_count.toLocaleString()}</Typography>
              </Box>
            )}

            {/* Last synced */}
            {status?.last_synced_at && (
              <Box>
                <Typography variant="caption" sx={{ color: theme.palette.text.disabled, display: 'block', fontSize: '0.62rem' }}>LAST SYNCED</Typography>
                <Typography variant="body2" sx={{ fontSize: '0.78rem' }} title={status.last_synced_at}>
                  {getRelativeTimeString(status.last_synced_at)}
                </Typography>
              </Box>
            )}

            {/* Duration */}
            {status?.duration_ms != null && (
              <Box>
                <Typography variant="caption" sx={{ color: theme.palette.text.disabled, display: 'block', fontSize: '0.62rem' }}>DURATION</Typography>
                <Typography variant="body2" sx={{ fontSize: '0.78rem' }}>{(status.duration_ms / 1000).toFixed(1)}s</Typography>
              </Box>
            )}

            {/* Sync button */}
            <Box sx={{ ml: 'auto' }}>
              <Button
                variant="outlined"
                size="small"
                startIcon={syncing ? <CircularProgress size={12} /> : <SyncIcon sx={{ fontSize: 14 }} />}
                onClick={handleSync}
                disabled={syncing}
                sx={{ fontSize: '0.72rem', height: 30, borderColor: theme.palette.divider }}
              >
                {syncing ? 'Syncing…' : 'Sync Now'}
              </Button>
            </Box>
          </Box>
        )}

        {/* Error message */}
        {status?.error_message && (
          <Typography variant="caption" sx={{ display: 'block', mt: 1.5, color: theme.palette.error.main, fontSize: '0.7rem' }}>
            {status.error_message}
          </Typography>
        )}

        {/* Inline error from failed sync attempt */}
        {error && (
          <Typography variant="caption" sx={{ display: 'block', mt: 1.5, color: theme.palette.error.main, fontSize: '0.7rem' }}>
            {error}
          </Typography>
        )}

        {/* First-sync hint */}
        {!status?.synced && !loading && (
          <Typography variant="caption" sx={{ display: 'block', mt: 1.5, color: theme.palette.text.disabled, fontSize: '0.68rem' }}>
            Run the first sync to populate technique names, tactic IDs, and ATT&amp;CK URLs across all alerts.
            The dataset is refreshed automatically every Sunday at 03:00 UTC.
          </Typography>
        )}
      </Box>
    </Box>
  );
};

// ── Page ──────────────────────────────────────────────────────────────────────

const AdminPage: React.FC = () => {
  const theme = useTheme();

  return (
    <Box sx={{ p: 3, maxWidth: 900 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>Admin</Typography>
        <Typography variant="body2" sx={{ color: theme.palette.text.secondary, fontSize: '0.78rem' }}>
          System configuration and data source management.
        </Typography>
      </Box>

      <Divider sx={{ mb: 3 }} />

      {/* Data Sources section */}
      <Typography variant="overline" sx={{ color: theme.palette.text.disabled, fontSize: '0.65rem', letterSpacing: '0.1em' }}>
        Data Sources
      </Typography>

      <Box sx={{ mt: 1.5 }}>
        <MitreSection />
      </Box>
    </Box>
  );
};

export default AdminPage;